import { FormikProps } from 'formik';
import { get, isEmpty } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import useFormUtils from 'hooks/useFormUtils';
import { POST_MIRROR_FIELDS_BY_FORM, PostMirrorField } from './postMirrorFields';

const isBlank = (value: unknown) => value === undefined || value === null || value === '';

type Props = {
    formik: FormikProps<any>;
    // The form starts out empty and is filled in once the declaration arrives - asking before that
    // would report every field as missing.
    isDeclarationLoaded: boolean;
    saveDeclaration: () => Promise<any>;
    onDone: () => void;
};

/**
 * Drives the completion prompt on a freshly mirrored declaration, for the master level fields that
 * could not be derived from the source declaration. Which fields those are depends on the declaration
 * that was generated - see `POST_MIRROR_FIELDS_BY_FORM`. Only the fields that actually came out empty
 * are offered, and if mirroring managed to fill them all in - or the declaration has none registered -
 * the user is never interrupted.
 *
 * Mounted only when there is a request to serve - see `usePostMirrorCompletionRequest`.
 */
const usePostMirrorCompletion = ({ formik, isDeclarationLoaded, saveDeclaration, onDone }: Props) => {
    const { formTypeForCds } = useFormUtils();

    const [fields, setFields] = useState<PostMirrorField[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isConfirmingDismiss, setIsConfirmingDismiss] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Which fields were missing is decided once, from the declaration as it was generated. Filling one
    // in must not make it disappear from under the user.
    const hasEvaluated = useRef(false);

    useEffect(() => {
        if (hasEvaluated.current) return;
        if (!isDeclarationLoaded || isEmpty(formik.values)) return;

        hasEvaluated.current = true;

        const formFields = POST_MIRROR_FIELDS_BY_FORM[formTypeForCds] ?? [];
        const missingFields = formFields.filter(({ path }) => isBlank(get(formik.values, path)));

        // Either mirroring derived them all, or this declaration has none registered - there is
        // nothing to interrupt the user with.
        if (isEmpty(missingFields)) {
            onDone();
            return;
        }

        setFields(missingFields);
        setIsOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formTypeForCds, isDeclarationLoaded, formik.values]);

    const touchFields = useCallback(
        (fieldsToTouch: PostMirrorField[]) => fieldsToTouch.forEach(({ path }) => formik.setFieldTouched(path, true)),
        [formik]
    );

    // A field is only highlighted in the form once it is both invalid and touched, and nothing on a
    // freshly loaded form is touched yet. Touching these is what makes good on the modal's promise
    // that they stay highlighted for the user to fill in on the form itself.
    const dismiss = useCallback(() => {
        setIsConfirmingDismiss(false);
        setIsOpen(false);
        touchFields(fields);
        onDone();
    }, [fields, touchFields, onDone]);

    const saveAndContinue = useCallback(async () => {
        setIsSaving(true);

        try {
            const errors = await formik.validateForm();
            const invalidFields = fields.filter(({ path }) => !!get(errors, path));

            if (!isEmpty(invalidFields)) {
                touchFields(invalidFields);
                return;
            }

            await saveDeclaration();
            setIsOpen(false);
            onDone();
        } finally {
            setIsSaving(false);
        }
    }, [formik, fields, touchFields, saveDeclaration, onDone]);

    return {
        isOpen,
        fields,
        isSaving,
        isConfirmingDismiss,
        requestDismiss: useCallback(() => setIsConfirmingDismiss(true), []),
        cancelDismiss: useCallback(() => setIsConfirmingDismiss(false), []),
        canSave: fields.every(({ path, required }) => !required || !isBlank(get(formik.values, path))),
        dismiss,
        saveAndContinue,
    };
};

export default usePostMirrorCompletion;
